import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Badge,
  Card,
  Button,
  Alert,
  Form,
  Spinner,
  Modal,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faDownload,
  faSpinner,
  faFileLines,
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useUser } from '../../../contexts/UserContext';
import {
  canManageDbqForms,
  canManageDbqRequests,
  canFinalizeDbqReview,
  canViewDbqAudit,
  isProviderUser,
} from '../../../lib/dbqPermissions';
import {
  useFetchUserDbqs,
  useFetchDbqClaimsAdmins,
  useCreateUserDbq,
  useUpdateUserDbqRequest,
  useDeleteUserDbq,
  useAcceptDbqSuggestion,
  useGenerateDbqPdf,
  useFinalizeDbqReview,
  useReopenFinalizedDbqReview,
  useRegenerateDbqSubmissionPacket,
  useSendClaimDbqsForQaReview,
  useSendBulkDbqEmail,
  useRequestDbqChanges,
  useRejectDbqSuggestion,
  useBulkMarkDbqsReadyForQaReview,
  downloadAllDbqSubmissionPackets,
  DBQ_STATUSES,
  DBQ_REQUEST_WORKFLOW_STATUSES,
} from '../../../services/userDbq';
import { useGetPresignedClaimFile } from '../../../services/claims';
import { useFetchDbqForms } from '../../../services/dbq';
import { fetchToeOptions } from '../../../services/medicalOpinion';
import {
  isCaseManagerReworkRound,
  isProviderReworkRound,
} from '../../../lib/dbqWorkflow';
import {
  EMPTY_DBQ_REQUEST_DRAFT,
  buildDbqRequestDraftFromUserDbq,
  buildDd214EvidenceWarning,
  ensureDd214SelectedEvidenceFileIds,
  getDbqReviewCounts,
  getDbqRequestStatusMeta,
  getPreferredDd214EvidenceFile,
  getWorkspaceConditionLabels,
  getWorkspaceConditionTitle,
  filterAiAutomatedFieldsByAccess,
  normalizeAiAutomatedFields,
} from './dbqWorkspaceUtils';
import {
  getDbqContentionDisplayLabel,
  serializeDbqContentions,
} from './dbqConditionOptions';
import DbqBulkSendModal from './DbqBulkSendModal';
import DbqBulkReviewReturnModal from './DbqBulkReviewReturnModal';
import DbqCaseManagerConditionCard from './DbqCaseManagerConditionCard';
import DbqRequestWorkspaceForm from './DbqRequestWorkspaceForm';
import DbqProviderWorkQueueCard from './DbqProviderWorkQueueCard';

const PROVIDER_ACTIONABLE_REQUEST_STATUSES = new Set([
  'sent_to_provider',
  'provider_in_progress',
  'completed',
]);

const CLAIM_REVIEW_READY_REQUEST_STATUSES = new Set([
  'in_qa_review',
  'finalized',
]);

const BULK_SEND_ELIGIBLE_REQUEST_STATUSES = new Set([
  'draft_request',
  'ready_to_send',
]);

const BULK_REQUEST_CHANGES_ELIGIBLE_REQUEST_STATUSES = new Set([
  'sent_to_provider',
  'provider_in_progress',
  'completed',
]);

const BULK_SEND_MODES = Object.freeze({
  send: 'send',
  requestChanges: 'request_changes',
});

const QA_REQUEST_CHANGE_TARGETS = Object.freeze({
  caseManager: 'case_manager',
  provider: 'provider',
});

const REOPEN_FINALIZED_DBQ_MODES = Object.freeze({
  returnToReview: 'return_to_review',
  requestChanges: 'request_changes',
});

const CARD_SHADOW = '0 1px 3px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.06)';
const CLAIM_APPROVED_STATUSES = new Set([
  'internal review approved',
  'submitted to va',
]);

const buildRequestContentionDrafts = (
  selectedContentions = [],
  existingRequestContentions = [],
) => {
  const existingAssignmentsByContention = new Map(
    (Array.isArray(existingRequestContentions) ? existingRequestContentions : [])
      .filter((requestContention) => requestContention?.contention)
      .map((requestContention) => [requestContention.contention, requestContention]),
  );

  return (Array.isArray(selectedContentions) ? selectedContentions : []).map((contention, index) => {
    const existingAssignment = existingAssignmentsByContention.get(contention);

    return {
      contention,
      contentionLabel:
        existingAssignment?.contentionLabel || getDbqContentionDisplayLabel(contention),
      toeId: existingAssignment?.toeId || '',
      sortOrder: index,
    };
  });
};

const getBulkSendEligibilityLabel = (userDbq, mode = BULK_SEND_MODES.send) => {
  const requestStatus = userDbq?.request?.status || null;
  if (!requestStatus) {
    return 'This request is missing request workspace status metadata.';
  }

  if (mode === BULK_SEND_MODES.requestChanges) {
    if (
      BULK_REQUEST_CHANGES_ELIGIBLE_REQUEST_STATUSES.has(requestStatus) ||
      isCaseManagerReworkRound(userDbq?.request) ||
      isProviderReworkRound(userDbq?.request)
    ) {
      return '';
    }

    return `Status is ${String(userDbq?.statusMeta?.label || requestStatus).toLowerCase()}. Request changes only supports DBQs that are already with the provider or have been returned from review for another provider round.`;
  }

  if (!BULK_SEND_ELIGIBLE_REQUEST_STATUSES.has(requestStatus)) {
    return `Status is ${String(userDbq?.statusMeta?.label || requestStatus).toLowerCase()}. Send to provider only supports requests that have not been sent yet.`;
  }

  return '';
};

const hasRequestGenerationFailure = (result = {}) =>
  Boolean(
    result?.moGenerationError ||
    result?.evidenceReviewGenerationError ||
    (Array.isArray(result?.dbqAiFieldGenerationResults) &&
      result.dbqAiFieldGenerationResults.some((entry) => Boolean(entry?.errorMessage))),
  );

const isReviewReturnEligible = (userDbq) =>
  Boolean(userDbq?.request?.status === 'changes_requested' && isCaseManagerReworkRound(userDbq?.request));

const DbqTab = ({ claimId, claimStatus = '', providers = [], claimFiles = [] }) => {
  const { user, roles, permissions } = useUser();
  const queryClient = useQueryClient();
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestMode, setRequestMode] = useState('create');
  const [requestDraft, setRequestDraft] = useState({ ...EMPTY_DBQ_REQUEST_DRAFT });
  const [collapsedConditionIds, setCollapsedConditionIds] = useState({});
  const [requestNotesDrafts, setRequestNotesDrafts] = useState({});
  const [showReviewNotificationModal, setShowReviewNotificationModal] = useState(false);
  const [selectedClaimsAdminUserIds, setSelectedClaimsAdminUserIds] = useState([]);
  const [qaChangesRequestTarget, setQaChangesRequestTarget] = useState(null);
  const [qaChangesRequestNote, setQaChangesRequestNote] = useState('');
  const [qaChangesRequestDestination, setQaChangesRequestDestination] = useState(
    QA_REQUEST_CHANGE_TARGETS.caseManager,
  );
  const [reopenDbqTarget, setReopenDbqTarget] = useState(null);
  const [reopenDbqMode, setReopenDbqMode] = useState(REOPEN_FINALIZED_DBQ_MODES.returnToReview);
  const [reopenDbqNote, setReopenDbqNote] = useState('');
  const [reopenDbqDestination, setReopenDbqDestination] = useState(
    QA_REQUEST_CHANGE_TARGETS.caseManager,
  );
  const [showBulkSendModal, setShowBulkSendModal] = useState(false);
  const [bulkSendMode, setBulkSendMode] = useState(BULK_SEND_MODES.send);
  const [bulkSendProviderUserId, setBulkSendProviderUserId] = useState('');
  const [selectedBulkSendUserDbqIds, setSelectedBulkSendUserDbqIds] = useState([]);
  const [showBulkReviewReturnModal, setShowBulkReviewReturnModal] = useState(false);
  const [selectedBulkReviewReturnUserDbqIds, setSelectedBulkReviewReturnUserDbqIds] = useState([]);
  const [bulkSendIncludeComments, setBulkSendIncludeComments] = useState(false);
  const [showDownloadAllPacketsModal, setShowDownloadAllPacketsModal] = useState(false);
  const [selectedDownloadPacketUserDbqIds, setSelectedDownloadPacketUserDbqIds] = useState([]);
  const [downloadingAllPackets, setDownloadingAllPackets] = useState(false);
  const [regeneratingPacketUserDbqId, setRegeneratingPacketUserDbqId] = useState(null);
  const [previewingPdfUserDbqId, setPreviewingPdfUserDbqId] = useState(null);
  const [hasAttemptedDd214AutoSelection, setHasAttemptedDd214AutoSelection] = useState(false);

  const dbqAccess = useMemo(
    () => ({ roles, permissions }),
    [permissions, roles],
  );
  const canManageRequests = canManageDbqRequests(dbqAccess);
  const canAccessDbqForms = canManageDbqForms(dbqAccess);
  const canLoadRequestForms = canManageRequests || canAccessDbqForms;
  const canLoadToeOptions = canManageRequests || canAccessDbqForms;
  const canAccessAudit = canViewDbqAudit(dbqAccess);
  const isProvider = isProviderUser(dbqAccess);
  const canReviewDbqs = canFinalizeDbqReview(dbqAccess);
  const isInternalRequestManagerView = canManageRequests && !isProvider;
  const isClaimsAdminView = canManageRequests && !isProvider && canReviewDbqs;

  const resetRequestDraft = useCallback(() => {
    setRequestMode('create');
    setRequestDraft({ ...EMPTY_DBQ_REQUEST_DRAFT });
    setHasAttemptedDd214AutoSelection(false);
  }, []);

  const closeRequestModal = useCallback(() => {
    setShowRequestModal(false);
    resetRequestDraft();
  }, [resetRequestDraft]);

  const providerOptions = useMemo(() => {
    return providers.map((provider) => ({
      userId: provider.userId,
      email: provider.userEmail,
      phone: provider.userPhone || null,
      name:
        `${provider.userFirstName || ''} ${provider.userLastName || ''}`.trim() ||
        provider.userEmail,
    }));
  }, [providers]);

  const providerOptionsById = useMemo(() => {
    return new Map(providerOptions.map((provider) => [provider.userId, provider]));
  }, [providerOptions]);

  const { data: claimsAdminsData, isLoading: isLoadingClaimsAdmins } = useFetchDbqClaimsAdmins(
    isInternalRequestManagerView,
  );
  const claimsAdminOptions = useMemo(() => {
    return (claimsAdminsData?.claimsAdmins || [])
      .map((claimsAdmin) => ({
        userId: claimsAdmin.userId,
        email: claimsAdmin.userEmail,
        name:
          `${claimsAdmin.userFirstName || ''} ${claimsAdmin.userLastName || ''}`.trim() ||
          claimsAdmin.userEmail,
      }));
  }, [claimsAdminsData?.claimsAdmins]);

  const { data: userDbqsData, isLoading: isLoadingUserDbqs } = useFetchUserDbqs(claimId);
  const expandedAiAutomatedFieldsEnabled = Boolean(
    userDbqsData?.dbqExpandedAiAutomatedFieldsEnabled,
  );
  const userDbqs = useMemo(() => userDbqsData?.userDbqs || [], [userDbqsData]);

  const { data: toeOptionsData, isLoading: isLoadingToeOptions } = useQuery({
    queryKey: ['toe-options'],
    queryFn: fetchToeOptions,
    enabled: canLoadToeOptions,
    refetchOnWindowFocus: false,
  });
  const toeOptions = useMemo(() => toeOptionsData?.toes || [], [toeOptionsData]);
  const reversedToeOptions = useMemo(() => [...toeOptions].reverse(), [toeOptions]);
  const toeOptionsById = useMemo(() => {
    return new Map(toeOptions.map((toe) => [toe.toeId, toe]));
  }, [toeOptions]);

  const { data: formsData, isLoading: isLoadingForms } = useFetchDbqForms(
    1,
    '',
    canLoadRequestForms,
  );
  const availableForms = useMemo(() => {
    return (formsData?.forms || []).filter((form) => form.docusealTemplateId);
  }, [formsData]);

  const dbqFormOptions = useMemo(() => {
    return [...availableForms]
      .sort((a, b) => (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' }))
      .map((form) => ({
        value: form.dbqFormId,
        label: form.title || form.shortName || form.dbqFormId,
        formId: form.formId || '',
        shortName: form.shortName || '',
        title: form.title || '',
      }));
  }, [availableForms]);

  const claimEvidenceFiles = useMemo(() => {
    return [...claimFiles]
      .filter((file) => file?.fileActive !== false)
      .sort((a, b) => {
        const aDate = a?.fileLastUpdated || a?.updatedAt || a?.createdAt || 0;
        const bDate = b?.fileLastUpdated || b?.updatedAt || b?.createdAt || 0;
        return new Date(bDate).getTime() - new Date(aDate).getTime();
      });
  }, [claimFiles]);

  const adminWorkspaceDbqs = useMemo(() => {
    return [...userDbqs]
      .sort(
        (a, b) =>
          new Date(b?.request?.updatedAt || b.updatedAt || b.createdAt || 0).getTime() -
          new Date(a?.request?.updatedAt || a.updatedAt || a.createdAt || 0).getTime(),
      )
      .map((userDbq) => ({
        ...userDbq,
        conditionLabels: getWorkspaceConditionLabels(userDbq),
        reviewCounts: getDbqReviewCounts(userDbq.formData || {}),
        conditionTitle: getWorkspaceConditionTitle(userDbq),
        statusMeta: getDbqRequestStatusMeta(
          userDbq,
          DBQ_REQUEST_WORKFLOW_STATUSES,
          DBQ_STATUSES,
        ),
      }));
  }, [userDbqs]);

  const caseManagerWorkspaceDbqs = useMemo(
    () => adminWorkspaceDbqs,
    [adminWorkspaceDbqs],
  );

  const readyForReviewDbqs = useMemo(() => {
    return caseManagerWorkspaceDbqs.filter(
      (userDbq) => userDbq?.request?.status === 'in_qa_review',
    );
  }, [caseManagerWorkspaceDbqs]);
  const reviewReturnEligibleDbqs = useMemo(() => {
    return caseManagerWorkspaceDbqs.filter((userDbq) => isReviewReturnEligible(userDbq));
  }, [caseManagerWorkspaceDbqs]);

  const finalizedPacketDbqs = useMemo(() => {
    return adminWorkspaceDbqs.filter(
      (userDbq) => userDbq?.request?.status === 'finalized',
    );
  }, [adminWorkspaceDbqs]);

  const nonFinalizedPacketDbqs = useMemo(() => {
    return adminWorkspaceDbqs.filter(
      (userDbq) => userDbq?.request?.status !== 'finalized',
    );
  }, [adminWorkspaceDbqs]);

  const canSendClaimForReview = useMemo(() => {
    if (!isInternalRequestManagerView || caseManagerWorkspaceDbqs.length === 0) return false;
    if (readyForReviewDbqs.length === 0) return false;

    return caseManagerWorkspaceDbqs.every((userDbq) => {
      const requestStatus = userDbq?.request?.status || null;
      return CLAIM_REVIEW_READY_REQUEST_STATUSES.has(requestStatus);
    });
  }, [caseManagerWorkspaceDbqs, isInternalRequestManagerView, readyForReviewDbqs]);

  const claimApprovalComplete = CLAIM_APPROVED_STATUSES.has(
    String(claimStatus || '').toLowerCase(),
  );

  const providerWorkspaceDbqs = useMemo(() => {
    return userDbqs
      .filter((userDbq) => {
        if (isProvider) {
          if (!userDbq.request?.providerUserId) return false;
          if (String(userDbq.request.providerUserId) !== String(user?.userId || '')) return false;
        }

        if (userDbq.request?.status) {
          if (userDbq.request.status === 'changes_requested') {
            return isProviderReworkRound(userDbq.request);
          }
          return PROVIDER_ACTIONABLE_REQUEST_STATUSES.has(userDbq.request.status);
        }
        return ['provider_ready', 'corrections', 'complete'].includes(userDbq.status || 'draft');
      })
      .sort(
        (a, b) =>
          new Date(b?.request?.updatedAt || b.updatedAt || b.createdAt || 0).getTime() -
          new Date(a?.request?.updatedAt || a.updatedAt || a.createdAt || 0).getTime(),
      )
      .map((userDbq) => ({
        ...userDbq,
        conditionLabels: getWorkspaceConditionLabels(userDbq),
        reviewCounts: getDbqReviewCounts(userDbq.formData || {}),
        conditionTitle: getWorkspaceConditionTitle(userDbq),
        statusMeta: getDbqRequestStatusMeta(
          userDbq,
          DBQ_REQUEST_WORKFLOW_STATUSES,
          DBQ_STATUSES,
        ),
      }));
  }, [isProvider, user?.userId, userDbqs]);

  const isBulkModeEligible = useCallback((userDbq, mode = BULK_SEND_MODES.send) => {
    const requestStatus = userDbq?.request?.status || '';

    if (mode === BULK_SEND_MODES.requestChanges) {
      return (
        BULK_REQUEST_CHANGES_ELIGIBLE_REQUEST_STATUSES.has(requestStatus) ||
        isCaseManagerReworkRound(userDbq?.request) ||
        isProviderReworkRound(userDbq?.request)
      );
    }

    return BULK_SEND_ELIGIBLE_REQUEST_STATUSES.has(requestStatus);
  }, []);

  const getEligibleBulkSendUserDbqIds = useCallback((providerUserId, mode = BULK_SEND_MODES.send) => {
    if (!providerUserId) return [];

    return caseManagerWorkspaceDbqs
      .filter((userDbq) => (
        String(userDbq?.request?.providerUserId || '') === String(providerUserId) &&
        isBulkModeEligible(userDbq, mode)
      ))
      .map((userDbq) => userDbq.userDbqId);
  }, [caseManagerWorkspaceDbqs, isBulkModeEligible]);

  const getBulkSendProviderOptionsForMode = useCallback((mode = BULK_SEND_MODES.send) => {
    return providerOptions.filter((provider) =>
      caseManagerWorkspaceDbqs.some(
        (userDbq) =>
          String(userDbq?.request?.providerUserId || '') === String(provider.userId) &&
          isBulkModeEligible(userDbq, mode),
      ),
    );
  }, [caseManagerWorkspaceDbqs, isBulkModeEligible, providerOptions]);

  const bulkSendProviderOptions = useMemo(() => {
    return getBulkSendProviderOptionsForMode(bulkSendMode);
  }, [bulkSendMode, getBulkSendProviderOptionsForMode]);
  const sendToProviderOptions = useMemo(() => {
    return getBulkSendProviderOptionsForMode(BULK_SEND_MODES.send);
  }, [getBulkSendProviderOptionsForMode]);
  const requestChangesProviderOptions = useMemo(() => {
    return getBulkSendProviderOptionsForMode(BULK_SEND_MODES.requestChanges);
  }, [getBulkSendProviderOptionsForMode]);

  const bulkSendDbqs = useMemo(() => {
    if (!bulkSendProviderUserId) return [];

    return caseManagerWorkspaceDbqs
      .filter(
        (userDbq) =>
          String(userDbq?.request?.providerUserId || '') === String(bulkSendProviderUserId),
      )
      .map((userDbq) => ({
        ...userDbq,
        isBulkSendEligible: isBulkModeEligible(userDbq, bulkSendMode),
        bulkSendEligibilityLabel: getBulkSendEligibilityLabel(userDbq, bulkSendMode),
      }));
  }, [bulkSendMode, bulkSendProviderUserId, caseManagerWorkspaceDbqs, isBulkModeEligible]);

  const createUserDbq = useCreateUserDbq((result) => {
    const message = result?.message || 'DBQ request created successfully.';

    if (hasRequestGenerationFailure(result)) {
      toast.warning(message);
    } else {
      toast.success(message);
    }

    closeRequestModal();
    queryClient.invalidateQueries({ queryKey: ['user-dbqs', claimId] });
  });

  const updateRequestWorkspace = useUpdateUserDbqRequest(
    (result) => {
      const message = result?.message || 'DBQ request workspace updated.';

      if (hasRequestGenerationFailure(result)) {
        toast.warning(message);
      } else {
        toast.success(message);
      }

      closeRequestModal();
      queryClient.invalidateQueries({ queryKey: ['user-dbqs', claimId] });
    },
    (error) => {
      toast.error(error?.response?.data?.msg || 'Failed to update DBQ request workspace');
    },
  );

  const deleteUserDbq = useDeleteUserDbq(() => {
    toast.success('DBQ request deleted successfully.');
    queryClient.invalidateQueries({ queryKey: ['user-dbqs', claimId] });
  });

  const sendBulkDbqEmailMutation = useSendBulkDbqEmail(
    (result) => {
      toast.success(result?.message || 'DBQ requests sent successfully.');
      setShowBulkSendModal(false);
      setBulkSendMode(BULK_SEND_MODES.send);
      setBulkSendIncludeComments(false);
      setBulkSendProviderUserId('');
      setSelectedBulkSendUserDbqIds([]);
      queryClient.invalidateQueries({ queryKey: ['user-dbqs', claimId] });
    },
    (error) => {
      toast.error(error?.response?.data?.msg || 'Failed to send DBQ requests');
    },
  );

  const acceptSuggestionMutation = useAcceptDbqSuggestion(
    () => {
      toast.success('Suggestion accepted.');
      queryClient.invalidateQueries({ queryKey: ['user-dbqs', claimId] });
    },
    (error) => {
      toast.error(error?.response?.data?.msg || 'Failed to accept suggestion');
    },
  );

  const rejectSuggestionMutation = useRejectDbqSuggestion(
    () => {
      toast.success('Suggestion rejected.');
      queryClient.invalidateQueries({ queryKey: ['user-dbqs', claimId] });
    },
    (error) => {
      toast.error(error?.response?.data?.msg || 'Failed to reject suggestion');
    },
  );

  const sendClaimDbqsForQaReviewMutation = useSendClaimDbqsForQaReview();
  const bulkMarkDbqsReadyForQaReviewMutation = useBulkMarkDbqsReadyForQaReview();
  const previewDbqPdfMutation = useGenerateDbqPdf();
  const finalizeDbqReviewMutation = useFinalizeDbqReview();
  const reopenFinalizedDbqReviewMutation = useReopenFinalizedDbqReview();
  const regeneratePacketMutation = useRegenerateDbqSubmissionPacket();
  const requestChangesMutation = useRequestDbqChanges();
  const openPreviewClaimFile = useGetPresignedClaimFile(
    (res) => {
      window.open(res.url, '_blank', 'noopener,noreferrer');
    },
    {
      onError: (error) => {
        toast.error(error?.response?.data?.msg || 'DBQ PDF preview generated, but it could not be opened.');
      },
    },
  );

  const isSavingRequest = createUserDbq.isPending || updateRequestWorkspace.isPending;
  const dd214EvidenceWarning = useMemo(
    () => buildDd214EvidenceWarning({
      claimEvidenceFiles,
      selectedEvidenceFileIds: requestDraft.selectedEvidenceFileIds,
    }),
    [claimEvidenceFiles, requestDraft.selectedEvidenceFileIds],
  );
  const isDraftRequestContext =
    requestMode === 'create' || requestDraft.requestStatus === 'draft_request';
  const hasCompleteRequestContentions = (
    Array.isArray(requestDraft.requestContentions) &&
    requestDraft.requestContentions.length > 0 &&
    requestDraft.requestContentions.length === requestDraft.selectedContentions.length &&
    requestDraft.requestContentions.every((requestContention) => requestContention?.toeId)
  );
  const canSubmitRequest = Boolean(
    requestDraft.dbqFormId &&
      requestDraft.selectedContentions.length > 0 &&
      hasCompleteRequestContentions &&
      requestDraft.providerUserId,
  );
  const canSaveDraft = Boolean(requestDraft.dbqFormId);

  const handleOpenCreateRequestModal = () => {
    setRequestMode('create');
    setRequestDraft({ ...EMPTY_DBQ_REQUEST_DRAFT });
    setHasAttemptedDd214AutoSelection(false);
    setShowRequestModal(true);
  };

  const handleOpenBulkSendModal = useCallback((mode = BULK_SEND_MODES.send) => {
    const nextProviderOptions = getBulkSendProviderOptionsForMode(mode);
    const initialProviderUserId = nextProviderOptions[0]?.userId || '';
    setBulkSendMode(mode);
    setBulkSendIncludeComments(false);
    setBulkSendProviderUserId(initialProviderUserId);
    setSelectedBulkSendUserDbqIds(getEligibleBulkSendUserDbqIds(initialProviderUserId, mode));
    setShowBulkSendModal(true);
  }, [getBulkSendProviderOptionsForMode, getEligibleBulkSendUserDbqIds]);

  const handleCloseBulkSendModal = useCallback(() => {
    setShowBulkSendModal(false);
    setBulkSendMode(BULK_SEND_MODES.send);
    setBulkSendIncludeComments(false);
    setBulkSendProviderUserId('');
    setSelectedBulkSendUserDbqIds([]);
  }, []);

  const handleOpenBulkReviewReturnModal = useCallback(() => {
    setSelectedBulkReviewReturnUserDbqIds(
      reviewReturnEligibleDbqs.map((userDbq) => userDbq.userDbqId),
    );
    setShowBulkReviewReturnModal(true);
  }, [reviewReturnEligibleDbqs]);

  const handleCloseBulkReviewReturnModal = useCallback(() => {
    if (bulkMarkDbqsReadyForQaReviewMutation.isPending) return;
    setShowBulkReviewReturnModal(false);
    setSelectedBulkReviewReturnUserDbqIds([]);
  }, [bulkMarkDbqsReadyForQaReviewMutation.isPending]);

  const handleToggleBulkReviewReturnUserDbq = useCallback((userDbqId, checked) => {
    setSelectedBulkReviewReturnUserDbqIds((prev) => {
      if (checked) {
        return [...new Set([...prev, userDbqId])];
      }
      return prev.filter((currentUserDbqId) => currentUserDbqId !== userDbqId);
    });
  }, []);

  const handleSelectAllEligibleBulkReviewReturn = useCallback(() => {
    setSelectedBulkReviewReturnUserDbqIds(
      reviewReturnEligibleDbqs.map((userDbq) => userDbq.userDbqId),
    );
  }, [reviewReturnEligibleDbqs]);

  const handleClearBulkReviewReturnSelection = useCallback(() => {
    setSelectedBulkReviewReturnUserDbqIds([]);
  }, []);

  const handleSubmitBulkReviewReturn = useCallback(() => {
    if (selectedBulkReviewReturnUserDbqIds.length === 0) {
      toast.error('Select at least one eligible DBQ request to return to review.');
      return;
    }

    bulkMarkDbqsReadyForQaReviewMutation.mutate(
      {
        claimId,
        userDbqIds: selectedBulkReviewReturnUserDbqIds,
      },
      {
        onSuccess: (result) => {
          toast.success(result?.message || 'Selected DBQs were returned to review.');
          setShowBulkReviewReturnModal(false);
          setSelectedBulkReviewReturnUserDbqIds([]);
          queryClient.invalidateQueries({ queryKey: ['user-dbqs', claimId] });
          queryClient.invalidateQueries({ queryKey: ['claim-profile', claimId] });
        },
        onError: (error) => {
          toast.error(error?.response?.data?.msg || 'Failed to return selected DBQs to review');
        },
      },
    );
  }, [
    bulkMarkDbqsReadyForQaReviewMutation,
    claimId,
    queryClient,
    selectedBulkReviewReturnUserDbqIds,
  ]);

  const handleBulkSendProviderChange = useCallback((providerUserId) => {
    setBulkSendProviderUserId(providerUserId);
    setSelectedBulkSendUserDbqIds(getEligibleBulkSendUserDbqIds(providerUserId, bulkSendMode));
  }, [bulkSendMode, getEligibleBulkSendUserDbqIds]);

  const handleToggleBulkSendUserDbq = useCallback((userDbqId, checked) => {
    setSelectedBulkSendUserDbqIds((prev) => {
      if (checked) {
        return [...new Set([...prev, userDbqId])];
      }

      return prev.filter((currentUserDbqId) => currentUserDbqId !== userDbqId);
    });
  }, []);

  const handleSelectAllEligibleBulkSend = useCallback(() => {
    setSelectedBulkSendUserDbqIds(
      getEligibleBulkSendUserDbqIds(bulkSendProviderUserId, bulkSendMode),
    );
  }, [bulkSendMode, bulkSendProviderUserId, getEligibleBulkSendUserDbqIds]);

  const handleClearBulkSendSelection = useCallback(() => {
    setSelectedBulkSendUserDbqIds([]);
  }, []);

  const handleSubmitBulkSend = useCallback(() => {
    if (!bulkSendProviderUserId) {
      toast.error('Please select a provider.');
      return;
    }

    if (selectedBulkSendUserDbqIds.length === 0) {
      toast.error(
        bulkSendMode === BULK_SEND_MODES.requestChanges
          ? 'Select at least one eligible DBQ request to resend.'
          : 'Select at least one eligible DBQ request to send.',
      );
      return;
    }

    sendBulkDbqEmailMutation.mutate({
      claimId,
      providerUserId: bulkSendProviderUserId,
      userDbqIds: selectedBulkSendUserDbqIds,
      includeComments: bulkSendIncludeComments,
      mode: bulkSendMode,
    });
  }, [
    bulkSendIncludeComments,
    bulkSendMode,
    bulkSendProviderUserId,
    claimId,
    selectedBulkSendUserDbqIds,
    sendBulkDbqEmailMutation,
  ]);

  const handleOpenEditRequestModal = (userDbq) => {
    setRequestMode('edit');
    setRequestDraft(buildDbqRequestDraftFromUserDbq(userDbq));
    setHasAttemptedDd214AutoSelection(false);
    setShowRequestModal(true);
  };

  useEffect(() => {
    if (!showRequestModal || hasAttemptedDd214AutoSelection) return;
    if (claimEvidenceFiles.length === 0) return;

    const dd214File = getPreferredDd214EvidenceFile(claimEvidenceFiles);
    setHasAttemptedDd214AutoSelection(true);

    if (!dd214File?.fileId) return;

    setRequestDraft((prev) => ({
      ...prev,
      selectedEvidenceFileIds: ensureDd214SelectedEvidenceFileIds(
        prev.selectedEvidenceFileIds,
        dd214File.fileId,
      ),
    }));
  }, [
    claimEvidenceFiles,
    hasAttemptedDd214AutoSelection,
    showRequestModal,
  ]);

  const handleRequestDraftChange = useCallback((field, value) => {
    if (field === 'selectedContentions') {
      setRequestDraft((prev) => {
        const nextSelectedContentions = Array.isArray(value) ? value : [];
        const nextRequestContentions = buildRequestContentionDrafts(
          nextSelectedContentions,
          prev.requestContentions,
        );

        return {
          ...prev,
          selectedContentions: nextSelectedContentions,
          requestContentions: nextRequestContentions,
          toeId: nextRequestContentions[0]?.toeId || '',
        };
      });
      return;
    }

    setRequestDraft((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleRequestContentionToeChange = useCallback((contention, toeId) => {
    setRequestDraft((prev) => {
      const nextRequestContentions = buildRequestContentionDrafts(
        prev.selectedContentions,
        prev.requestContentions,
      ).map((requestContention) =>
        requestContention.contention === contention
          ? {
              ...requestContention,
              toeId,
            }
          : requestContention,
      );

      return {
        ...prev,
        requestContentions: nextRequestContentions,
        toeId: nextRequestContentions[0]?.toeId || '',
      };
    });
  }, []);

  const handleEvidenceToggle = useCallback((fileId, checked) => {
    setRequestDraft((prev) => ({
      ...prev,
      selectedEvidenceFileIds: checked
        ? [...new Set([...prev.selectedEvidenceFileIds, fileId])]
        : prev.selectedEvidenceFileIds.filter((currentFileId) => currentFileId !== fileId),
    }));
  }, []);

  const handleSubmitRequest = (submissionMode = 'submit') => {
    const normalizedRequestContentions = buildRequestContentionDrafts(
      requestDraft.selectedContentions,
      requestDraft.requestContentions,
    );
    const isDraftSave = submissionMode === 'draft';
    const shouldTransitionDraftToRequest =
      !isDraftSave &&
      (requestMode === 'create' || requestDraft.requestStatus === 'draft_request');

    if (!requestDraft.dbqFormId) {
      toast.error('Please select a DBQ form.');
      return;
    }

    if (!isDraftSave && normalizedRequestContentions.length === 0) {
      toast.error('Please select at least one contention.');
      return;
    }

    if (
      !isDraftSave &&
      normalizedRequestContentions.some((requestContention) => !requestContention.toeId)
    ) {
      toast.error('Please assign a Theory of Entitlement (TOE) for every contention.');
      return;
    }

    if (!isDraftSave && !requestDraft.providerUserId) {
      toast.error('Please select an assigned provider.');
      return;
    }

    const aiAutomatedFields = normalizeAiAutomatedFields(requestDraft.aiAutomatedFields, {
      evidenceReviewPrompt: requestDraft.evidenceReviewPrompt,
      autoGenerateMedicalOpinion: requestDraft.autoGenerateMedicalOpinion,
      moPrompt: requestDraft.moPrompt,
    });
    const requestAiAutomatedFields = filterAiAutomatedFieldsByAccess(aiAutomatedFields, {
      expandedAiAutomatedFieldsEnabled,
    });
    const evidenceReviewAutomation = aiAutomatedFields.evidence_review || {
      enabled: true,
      prompt: '',
    };
    const medicalOpinionAutomation = aiAutomatedFields.medical_opinion || {
      enabled: true,
      prompt: '',
    };

    const requestPayload = {
      toeId: normalizedRequestContentions[0]?.toeId || null,
      contention:
        serializeDbqContentions(
          normalizedRequestContentions.map((requestContention) => requestContention.contention),
        ) || null,
      requestContentions: normalizedRequestContentions,
      requestNotes: requestDraft.requestNotes || null,
      providerUserId: requestDraft.providerUserId || null,
      selectedEvidenceFileIds: requestDraft.selectedEvidenceFileIds,
      aiFieldGenerationSettings: requestAiAutomatedFields,
      evidenceReviewPrompt: expandedAiAutomatedFieldsEnabled
        ? evidenceReviewAutomation.prompt || ''
        : '',
      medicalOpinionParameters: {
        autoGenerateMedicalOpinion: medicalOpinionAutomation.enabled !== false,
        prompt: medicalOpinionAutomation.prompt || null,
        createPdf: false,
        insertIntoDbq: true,
      },
      autoGenerateMedicalOpinion: medicalOpinionAutomation.enabled !== false,
      moPrompt: medicalOpinionAutomation.prompt || '',
      ...(isDraftSave ? { saveAsDraft: true } : {}),
      ...(shouldTransitionDraftToRequest ? { submitRequest: true } : {}),
    };

    if (requestMode === 'edit' && requestDraft.userDbqId) {
      updateRequestWorkspace.mutate({
        userDbqId: requestDraft.userDbqId,
        ...requestPayload,
      });
      return;
    }

    createUserDbq.mutate({
      claimId,
      dbqFormId: requestDraft.dbqFormId,
      ...requestPayload,
    });
  };

  const handleOpenEditor = (userDbq) => {
    window.open(`/dbq/edit/${userDbq.userDbqId}`, '_blank');
  };

  const handleAcceptProviderSuggestion = useCallback((userDbqId, suggestionId) => {
    acceptSuggestionMutation.mutate({ userDbqId, suggestionId });
  }, [acceptSuggestionMutation]);

  const handleRejectProviderSuggestion = useCallback((userDbqId, suggestionId) => {
    rejectSuggestionMutation.mutate({ userDbqId, suggestionId });
  }, [rejectSuggestionMutation]);

  const handleOpenSendForReviewModal = useCallback(() => {
    if (!canSendClaimForReview) return;
    // Nobody preselected (2026-08-05): the all-checked default meant "open,
    // click send" blasted every claims:approve_for_va holder on every claim.
    // The CM picks who reviews; Select all remains one click away.
    setSelectedClaimsAdminUserIds([]);
    setShowReviewNotificationModal(true);
  }, [canSendClaimForReview]);

  const handleToggleClaimsAdminSelection = useCallback((claimsAdminUserId, checked) => {
    setSelectedClaimsAdminUserIds((prev) => {
      if (checked) {
        return [...new Set([...prev, claimsAdminUserId])];
      }
      return prev.filter((userId) => userId !== claimsAdminUserId);
    });
  }, []);

  const handleSendClaimForReview = useCallback(() => {
    if (!canSendClaimForReview) return;
    if (selectedClaimsAdminUserIds.length === 0) {
      toast.error('Select at least one Claims Admin to notify.');
      return;
    }

    sendClaimDbqsForQaReviewMutation.mutate(
      {
        claimId,
        claimsAdminUserIds: selectedClaimsAdminUserIds,
      },
      {
        onSuccess: (result) => {
          toast.success(
            result?.message || 'Claim sent for Claims Admin review.',
          );
          setShowReviewNotificationModal(false);
          queryClient.invalidateQueries({ queryKey: ['user-dbqs', claimId] });
        },
        onError: (error) => {
          toast.error(error?.response?.data?.msg || 'Failed to send claim for Claims Admin review');
        },
      },
    );
  }, [
    canSendClaimForReview,
    claimId,
    queryClient,
    selectedClaimsAdminUserIds,
    sendClaimDbqsForQaReviewMutation,
  ]);

  const handleOpenQaChangesModal = useCallback((userDbq) => {
    setQaChangesRequestTarget(userDbq);
    setQaChangesRequestNote('');
    setQaChangesRequestDestination(QA_REQUEST_CHANGE_TARGETS.caseManager);
  }, []);

  const closeQaChangesModal = useCallback(() => {
    setQaChangesRequestTarget(null);
    setQaChangesRequestNote('');
    setQaChangesRequestDestination(QA_REQUEST_CHANGE_TARGETS.caseManager);
  }, []);

  const handleRequestQaChanges = useCallback(() => {
    if (!qaChangesRequestTarget?.userDbqId) return;

    requestChangesMutation.mutate(
      {
        userDbqId: qaChangesRequestTarget.userDbqId,
        includeComments: false,
        sendSms: false,
        recipientPhone: null,
        note: qaChangesRequestNote,
        target: qaChangesRequestDestination,
      },
      {
        onSuccess: (result) => {
          toast.success(
            result?.message ||
              (
                qaChangesRequestDestination === QA_REQUEST_CHANGE_TARGETS.provider
                  ? 'Changes requested and provider notified.'
                  : 'Changes requested and claim manager notified.'
              ),
          );
          closeQaChangesModal();
          queryClient.invalidateQueries({ queryKey: ['user-dbqs', claimId] });
        },
        onError: (error) => {
          toast.error(error?.response?.data?.msg || 'Failed to request changes');
        },
      },
    );
  }, [
    claimId,
    closeQaChangesModal,
    qaChangesRequestNote,
    qaChangesRequestDestination,
    qaChangesRequestTarget?.userDbqId,
    queryClient,
    requestChangesMutation,
  ]);

  const handleFinalizeQaReview = useCallback((userDbq) => {
    finalizeDbqReviewMutation.mutate(userDbq.userDbqId, {
      onSuccess: (result) => {
        toast.success(result?.message || 'DBQ marked finalized.');
        queryClient.invalidateQueries({ queryKey: ['user-dbqs', claimId] });
        queryClient.invalidateQueries({ queryKey: ['claim-profile', claimId] });
      },
      onError: (error) => {
        toast.error(error?.response?.data?.msg || 'Failed to finalize DBQ review');
      },
    });
  }, [claimId, finalizeDbqReviewMutation, queryClient]);

  const handleOpenReopenDbqModal = useCallback((userDbq) => {
    setReopenDbqTarget(userDbq);
    setReopenDbqMode(REOPEN_FINALIZED_DBQ_MODES.returnToReview);
    setReopenDbqNote('');
    setReopenDbqDestination(QA_REQUEST_CHANGE_TARGETS.caseManager);
  }, []);

  const closeReopenDbqModal = useCallback(() => {
    setReopenDbqTarget(null);
    setReopenDbqMode(REOPEN_FINALIZED_DBQ_MODES.returnToReview);
    setReopenDbqNote('');
    setReopenDbqDestination(QA_REQUEST_CHANGE_TARGETS.caseManager);
  }, []);

  const handleReopenFinalizedDbq = useCallback(() => {
    if (!reopenDbqTarget?.userDbqId) return;
    if (!String(reopenDbqNote || '').trim()) {
      toast.error('Please add a reason before reopening this DBQ.');
      return;
    }

    reopenFinalizedDbqReviewMutation.mutate(
      {
        userDbqId: reopenDbqTarget.userDbqId,
        mode: reopenDbqMode,
        target:
          reopenDbqMode === REOPEN_FINALIZED_DBQ_MODES.requestChanges
            ? reopenDbqDestination
            : null,
        note: reopenDbqNote,
      },
      {
        onSuccess: (result) => {
          toast.success(result?.message || 'DBQ reopened successfully.');
          closeReopenDbqModal();
          queryClient.invalidateQueries({ queryKey: ['user-dbqs', claimId] });
          queryClient.invalidateQueries({ queryKey: ['claim-profile', claimId] });
        },
        onError: (error) => {
          toast.error(error?.response?.data?.msg || 'Failed to reopen finalized DBQ');
        },
      },
    );
  }, [
    claimId,
    closeReopenDbqModal,
    queryClient,
    reopenDbqDestination,
    reopenDbqMode,
    reopenDbqNote,
    reopenDbqTarget?.userDbqId,
    reopenFinalizedDbqReviewMutation,
  ]);

  const handleRegeneratePacket = useCallback((userDbq) => {
    if (!userDbq?.userDbqId) return;

    setRegeneratingPacketUserDbqId(userDbq.userDbqId);
    regeneratePacketMutation.mutate(userDbq.userDbqId, {
      onSuccess: (result) => {
        toast.success(result?.message || 'Submission packet regenerated.');
        queryClient.invalidateQueries({ queryKey: ['user-dbqs', claimId] });
        queryClient.invalidateQueries({ queryKey: ['claim-profile', claimId] });
      },
      onError: (error) => {
        toast.error(error?.response?.data?.msg || 'Failed to regenerate submission packet');
      },
      onSettled: () => {
        setRegeneratingPacketUserDbqId(null);
      },
    });
  }, [claimId, queryClient, regeneratePacketMutation]);

  const handlePreviewDbqPdf = useCallback((userDbq) => {
    if (!userDbq?.userDbqId) return;

    setPreviewingPdfUserDbqId(userDbq.userDbqId);
    previewDbqPdfMutation.mutate(userDbq.userDbqId, {
      onSuccess: (result) => {
        toast.success('DBQ PDF preview generated successfully.');
        queryClient.invalidateQueries({ queryKey: ['user-dbqs', claimId] });
        queryClient.invalidateQueries({ queryKey: ['claim-profile', claimId] });

        if (result?.fileId) {
          openPreviewClaimFile.mutate({
            claimId: userDbq.claimId || claimId,
            fileId: result.fileId,
          });
        }
      },
      onError: (error) => {
        toast.error(error?.response?.data?.msg || 'Failed to preview DBQ PDF');
      },
      onSettled: () => {
        setPreviewingPdfUserDbqId(null);
      },
    });
  }, [claimId, openPreviewClaimFile, previewDbqPdfMutation, queryClient]);

  const handleDelete = (userDbq) => {
    const confirmed = window.confirm(
      `Delete "${userDbq.title}" and all request data?\n\nThis cannot be undone.`,
    );
    if (!confirmed) return;

    const doubleConfirmed = window.confirm(
      'Please confirm: this permanently deletes the DBQ request and its form data.',
    );
    if (doubleConfirmed) {
      deleteUserDbq.mutate(userDbq.userDbqId);
    }
  };

  const handleOpenDownloadAllPacketsModal = useCallback(() => {
    setSelectedDownloadPacketUserDbqIds(
      finalizedPacketDbqs.map((userDbq) => userDbq.userDbqId),
    );
    setShowDownloadAllPacketsModal(true);
  }, [finalizedPacketDbqs]);

  const handleCloseDownloadAllPacketsModal = useCallback(() => {
    if (downloadingAllPackets) return;
    setShowDownloadAllPacketsModal(false);
    setSelectedDownloadPacketUserDbqIds([]);
  }, [downloadingAllPackets]);

  const handleToggleDownloadPacketSelection = useCallback((userDbqId, checked) => {
    setSelectedDownloadPacketUserDbqIds((prev) => {
      if (checked) {
        return [...new Set([...prev, userDbqId])];
      }
      return prev.filter((currentUserDbqId) => currentUserDbqId !== userDbqId);
    });
  }, []);

  const handleSelectAllDownloadPackets = useCallback(() => {
    setSelectedDownloadPacketUserDbqIds(
      finalizedPacketDbqs.map((userDbq) => userDbq.userDbqId),
    );
  }, [finalizedPacketDbqs]);

  const handleClearDownloadPacketSelection = useCallback(() => {
    setSelectedDownloadPacketUserDbqIds([]);
  }, []);

  const handleDownloadAllPackets = useCallback(async () => {
    if (!claimId) return;
    if (selectedDownloadPacketUserDbqIds.length === 0) {
      toast.error('Select at least one finalized request to include.');
      return;
    }

    setDownloadingAllPackets(true);
    try {
      await downloadAllDbqSubmissionPackets(claimId, selectedDownloadPacketUserDbqIds);
      setShowDownloadAllPacketsModal(false);
      setSelectedDownloadPacketUserDbqIds([]);
    } finally {
      setDownloadingAllPackets(false);
    }
  }, [claimId, selectedDownloadPacketUserDbqIds]);

  const handleToggleConditionCollapse = useCallback((userDbqId) => {
    setCollapsedConditionIds((prev) => ({
      ...prev,
      [userDbqId]: !(prev[userDbqId] ?? true),
    }));
  }, []);

  const inlineNotesMutation = useUpdateUserDbqRequest(
    (_result, variables) => {
      toast.success('Notes updated.');
      setRequestNotesDrafts((prev) => ({
        ...prev,
        [variables.userDbqId]: variables.requestNotes || '',
      }));
      queryClient.invalidateQueries({ queryKey: ['user-dbqs', claimId] });
    },
    (error) => {
      toast.error(error?.response?.data?.msg || 'Failed to update notes');
    },
  );

  const handleRequestNotesDraftChange = useCallback((userDbqId, value) => {
    setRequestNotesDrafts((prev) => ({
      ...prev,
      [userDbqId]: value,
    }));
  }, []);

  const handleSaveRequestNotes = useCallback((userDbq) => {
    const request = userDbq.request || {};
    const nextNotes = String(
      requestNotesDrafts[userDbq.userDbqId] ?? request.requestNotes ?? '',
    );

    inlineNotesMutation.mutate({
      userDbqId: userDbq.userDbqId,
      requestNotes: nextNotes || null,
    });
  }, [inlineNotesMutation, requestNotesDrafts]);

  if (
    isLoadingUserDbqs ||
    (canLoadRequestForms && isLoadingForms) ||
    (isInternalRequestManagerView && isLoadingClaimsAdmins)
  ) {
    return (
      <Card className="border-0" style={{ boxShadow: CARD_SHADOW }}>
        <Card.Body className="text-center py-5">
          <Spinner animation="border" size="sm" />
          <p className="mt-3 text-muted mb-0" style={{ fontSize: '0.85rem' }}>
            Loading DBQ workspace...
          </p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
        <div>
          <h5 className="mb-1 fw-semibold">Condition Requests</h5>
          {canManageRequests && adminWorkspaceDbqs.length > 0 ? (
            <div className="text-muted" style={{ fontSize: '0.82rem' }}>
              {adminWorkspaceDbqs.length} request
              {adminWorkspaceDbqs.length !== 1 ? 's' : ''}
            </div>
          ) : null}
          {claimApprovalComplete && isInternalRequestManagerView ? (
            <div style={{ fontSize: '0.8rem', color: '#15803d' }}>
              Claim approved. Submission packets are ready to review and download.
            </div>
          ) : null}
        </div>

        <div className="d-flex align-items-center gap-2 flex-wrap">
          {(isInternalRequestManagerView &&
            finalizedPacketDbqs.length > 0) ? (
            <Button
              variant="outline-dark"
              onClick={handleOpenDownloadAllPacketsModal}
              disabled={downloadingAllPackets}
            >
              {downloadingAllPackets ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                  Preparing Packets...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faDownload} className="me-2" />
                  Download All Packets
                </>
              )}
            </Button>
          ) : null}

        {isInternalRequestManagerView ? (
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {canSendClaimForReview ? (
              <Button
                variant="dark"
                onClick={handleOpenSendForReviewModal}
                disabled={sendClaimDbqsForQaReviewMutation.isPending}
              >
                {sendClaimDbqsForQaReviewMutation.isPending ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                    Sending...
                  </>
                ) : (
                  'Send For Review'
                )}
              </Button>
            ) : null}
            {canLoadRequestForms && availableForms.length > 0 ? (
              <>
                {sendToProviderOptions.length > 0 ? (
                  <Button
                    variant="outline-primary"
                    onClick={() => handleOpenBulkSendModal(BULK_SEND_MODES.send)}
                    disabled={sendBulkDbqEmailMutation.isPending}
                  >
                    Send To Provider
                  </Button>
                ) : null}
                {requestChangesProviderOptions.length > 0 ? (
                  <Button
                    variant="outline-secondary"
                    onClick={() => handleOpenBulkSendModal(BULK_SEND_MODES.requestChanges)}
                    disabled={sendBulkDbqEmailMutation.isPending}
                  >
                    Request Changes
                  </Button>
                ) : null}
                {reviewReturnEligibleDbqs.length > 0 ? (
                  <Button
                    variant="outline-success"
                    onClick={handleOpenBulkReviewReturnModal}
                    disabled={bulkMarkDbqsReadyForQaReviewMutation.isPending}
                  >
                    Return To Review
                  </Button>
                ) : null}
                <Button
                  variant="primary"
                  onClick={handleOpenCreateRequestModal}
                  disabled={isSavingRequest}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  New Request
                </Button>
              </>
            ) : null}
          </div>
        ) : null}
        </div>
      </div>

      {isInternalRequestManagerView && canLoadRequestForms && availableForms.length === 0 ? (
        <Alert variant="warning">
          No DBQ forms with templates are configured yet. Configure templates in the DBQ Library
          before creating requests.
        </Alert>
      ) : null}

      {isInternalRequestManagerView ? (
        caseManagerWorkspaceDbqs.length === 0 ? (
          <Card
            className="border-0 text-center py-5"
            style={{ boxShadow: CARD_SHADOW }}
          >
            <Card.Body>
              <div className="text-muted mb-3" style={{ fontSize: '2rem', opacity: 0.3 }}>
                <FontAwesomeIcon icon={faFileLines} />
              </div>
              <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                No condition requests yet. Click <strong>New Request</strong> to get started.
              </p>
            </Card.Body>
          </Card>
        ) : (
          <div className="d-flex flex-column gap-3">
            {caseManagerWorkspaceDbqs.map((userDbq) => {
              const request = userDbq.request || {};
              const currentRequestNotes =
                requestNotesDrafts[userDbq.userDbqId] ?? request.requestNotes ?? '';
              const hasUnsavedNotes =
                String(currentRequestNotes) !== String(request.requestNotes || '');

              return (
                <DbqCaseManagerConditionCard
                  key={userDbq.userDbqId}
                  claimId={claimId}
                  userDbq={userDbq}
                  isCollapsed={collapsedConditionIds[userDbq.userDbqId] ?? true}
                  toeOptionsById={toeOptionsById}
                  providerOptionsById={providerOptionsById}
                  currentRequestNotes={currentRequestNotes}
                  hasUnsavedNotes={hasUnsavedNotes}
                  isSavingNotes={inlineNotesMutation.isPending}
                  isDeleting={deleteUserDbq.isPending}
                  canAccessAudit={canAccessAudit}
                  onToggleCollapse={handleToggleConditionCollapse}
                  onOpenEditor={handleOpenEditor}
                  onOpenEditRequest={handleOpenEditRequestModal}
                  onDelete={handleDelete}
                  onPreviewPdf={handlePreviewDbqPdf}
                  isPreviewingPdf={previewingPdfUserDbqId === userDbq.userDbqId}
                  onRegeneratePacket={handleRegeneratePacket}
                  isRegeneratingPacket={regeneratingPacketUserDbqId === userDbq.userDbqId}
                  onRequestNotesChange={handleRequestNotesDraftChange}
                  onSaveRequestNotes={handleSaveRequestNotes}
                  onRequestChanges={isClaimsAdminView ? handleOpenQaChangesModal : null}
                  isRequestingChanges={isClaimsAdminView && requestChangesMutation.isPending}
                  onFinalize={isClaimsAdminView ? handleFinalizeQaReview : null}
                  isFinalizing={isClaimsAdminView && finalizeDbqReviewMutation.isPending}
                  onReopen={isClaimsAdminView ? handleOpenReopenDbqModal : null}
                  isReopening={isClaimsAdminView && reopenFinalizedDbqReviewMutation.isPending}
                  showQaActions={isClaimsAdminView}
                />
              );
            })}
          </div>
        )
      ) : providerWorkspaceDbqs.length === 0 ? (
        <Card
          className="border-0 text-center py-5"
          style={{ boxShadow: CARD_SHADOW }}
        >
          <Card.Body>
            <div className="text-muted mb-3" style={{ fontSize: '2rem', opacity: 0.3 }}>
              <FontAwesomeIcon icon={faFileLines} />
            </div>
            <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
              No DBQ requests are currently actionable.
            </p>
          </Card.Body>
        </Card>
      ) : (
        <div className="d-flex flex-column gap-3">
          {providerWorkspaceDbqs.map((userDbq) => (
            <DbqProviderWorkQueueCard
              key={userDbq.userDbqId}
              userDbq={userDbq}
              toeOptionsById={toeOptionsById}
              onOpenEditor={handleOpenEditor}
              onPreviewPdf={handlePreviewDbqPdf}
              isPreviewingPdf={previewingPdfUserDbqId === userDbq.userDbqId}
              onAcceptSuggestion={handleAcceptProviderSuggestion}
              onRejectSuggestion={handleRejectProviderSuggestion}
              isUpdatingSuggestions={
                acceptSuggestionMutation.isPending || rejectSuggestionMutation.isPending
              }
            />
          ))}
        </div>
      )}

      <Modal
        show={showDownloadAllPacketsModal}
        onHide={handleCloseDownloadAllPacketsModal}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1.05rem' }}>Download All Packets</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted" style={{ fontSize: '0.9rem' }}>
            Choose which finalized requests to include. Finalized requests are selected by
            default. Unfinalized requests are shown below for reference only.
          </p>

          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
            <div className="small text-muted">
              {finalizedPacketDbqs.length} finalized request
              {finalizedPacketDbqs.length !== 1 ? 's' : ''} available
            </div>
            <div className="d-flex align-items-center gap-2">
              <Button
                variant="link"
                size="sm"
                className="p-0 text-decoration-none"
                onClick={handleSelectAllDownloadPackets}
                disabled={downloadingAllPackets || finalizedPacketDbqs.length === 0}
              >
                Select all finalized
              </Button>
              <Button
                variant="link"
                size="sm"
                className="p-0 text-decoration-none"
                onClick={handleClearDownloadPacketSelection}
                disabled={downloadingAllPackets || selectedDownloadPacketUserDbqIds.length === 0}
              >
                Clear
              </Button>
            </div>
          </div>

          <div className="border rounded overflow-hidden">
            {finalizedPacketDbqs.map((userDbq) => (
              <div
                key={userDbq.userDbqId}
                className="d-flex align-items-start gap-3 p-3 border-bottom"
              >
                <Form.Check
                  type="checkbox"
                  className="mt-1"
                  checked={selectedDownloadPacketUserDbqIds.includes(userDbq.userDbqId)}
                  disabled={downloadingAllPackets}
                  onChange={(event) =>
                    handleToggleDownloadPacketSelection(userDbq.userDbqId, event.target.checked)
                  }
                />
                <div className="flex-grow-1 min-width-0">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <div className="fw-semibold">
                      {userDbq.conditionTitle || userDbq.title || 'Untitled DBQ'}
                    </div>
                    <Badge bg="success">Finalized</Badge>
                  </div>
                  <div className="text-muted small mt-1">
                    {userDbq.title || 'Untitled DBQ'}
                  </div>
                </div>
              </div>
            ))}

            {nonFinalizedPacketDbqs.length > 0 ? (
              <>
                <div className="px-3 py-2 border-top border-bottom bg-light text-muted small fw-semibold">
                  Other Requests
                </div>
                {nonFinalizedPacketDbqs.map((userDbq) => (
                  <div
                    key={userDbq.userDbqId}
                    className="d-flex align-items-start gap-3 p-3 border-bottom"
                    style={{ background: '#f8f9fb' }}
                  >
                    <Form.Check
                      type="checkbox"
                      className="mt-1"
                      checked={false}
                      disabled
                      readOnly
                    />
                    <div className="flex-grow-1 min-width-0">
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <div className="fw-semibold">
                          {userDbq.conditionTitle || userDbq.title || 'Untitled DBQ'}
                        </div>
                        <Badge
                          bg={userDbq.statusMeta?.variant === 'gray'
                            ? 'light'
                            : (userDbq.statusMeta?.variant || 'secondary')}
                          text={userDbq.statusMeta?.variant === 'gray' ? 'dark' : undefined}
                        >
                          {userDbq.statusMeta?.label || 'Draft'}
                        </Badge>
                      </div>
                      <div className="text-muted small mt-1">
                        Shown for reference only. This request is not finalized yet, so it will not
                        be included in the download.
                      </div>
                    </div>
                  </div>
                ))}
              </>
            ) : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCloseDownloadAllPacketsModal}
            disabled={downloadingAllPackets}
          >
            Cancel
          </Button>
          <Button
            variant="dark"
            onClick={handleDownloadAllPackets}
            disabled={downloadingAllPackets || selectedDownloadPacketUserDbqIds.length === 0}
          >
            {downloadingAllPackets ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                Preparing Packets...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faDownload} className="me-2" />
                Download Selected Packets
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <DbqBulkSendModal
        show={showBulkSendModal}
        onHide={handleCloseBulkSendModal}
        mode={bulkSendMode}
        providerOptions={bulkSendProviderOptions}
        providerUserId={bulkSendProviderUserId}
        onProviderChange={handleBulkSendProviderChange}
        dbqs={bulkSendDbqs}
        selectedUserDbqIds={selectedBulkSendUserDbqIds}
        onToggleUserDbq={handleToggleBulkSendUserDbq}
        onSelectAllEligible={handleSelectAllEligibleBulkSend}
        onClearSelection={handleClearBulkSendSelection}
        includeComments={bulkSendIncludeComments}
        onIncludeCommentsChange={setBulkSendIncludeComments}
        onSubmit={handleSubmitBulkSend}
        isSending={sendBulkDbqEmailMutation.isPending}
      />

      <DbqBulkReviewReturnModal
        show={showBulkReviewReturnModal}
        onHide={handleCloseBulkReviewReturnModal}
        dbqs={reviewReturnEligibleDbqs}
        selectedUserDbqIds={selectedBulkReviewReturnUserDbqIds}
        onToggleUserDbq={handleToggleBulkReviewReturnUserDbq}
        onSelectAllEligible={handleSelectAllEligibleBulkReviewReturn}
        onClearSelection={handleClearBulkReviewReturnSelection}
        onSubmit={handleSubmitBulkReviewReturn}
        isSubmitting={bulkMarkDbqsReadyForQaReviewMutation.isPending}
      />

      {/* Create / Edit request modal */}
      <Modal show={showRequestModal} onHide={closeRequestModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1.1rem' }}>
            {requestMode === 'edit' ? 'Edit DBQ Request' : 'Create DBQ Request'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {requestMode === 'create' ? (
            <div className="mb-3 text-muted" style={{ fontSize: '0.85rem' }}>
              Select the DBQ, choose one or more claim contentions, assign the provider, and
              package the request for provider work.
            </div>
          ) : null}
          <DbqRequestWorkspaceForm
            draft={requestDraft}
            mode={requestMode}
            dbqFormOptions={dbqFormOptions}
            toeOptions={reversedToeOptions}
            providerOptions={providerOptions}
            claimEvidenceFiles={claimEvidenceFiles}
            isSavingRequest={isSavingRequest}
            isLoadingToeOptions={isLoadingToeOptions}
            dd214EvidenceWarning={dd214EvidenceWarning}
            expandedAiAutomatedFieldsEnabled={expandedAiAutomatedFieldsEnabled}
            onFieldChange={handleRequestDraftChange}
            onRequestContentionToeChange={handleRequestContentionToeChange}
            onEvidenceToggle={handleEvidenceToggle}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeRequestModal} disabled={isSavingRequest}>
            Cancel
          </Button>
          {isDraftRequestContext ? (
            <Button
              variant="outline-secondary"
              onClick={() => handleSubmitRequest('draft')}
              disabled={isSavingRequest || !canSaveDraft}
            >
              Save Draft
            </Button>
          ) : null}
          <Button
            variant="primary"
            onClick={() => handleSubmitRequest('submit')}
            disabled={isSavingRequest || !canSubmitRequest}
          >
            {isSavingRequest ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                Saving...
              </>
            ) : (
              isDraftRequestContext
                ? 'Create Request'
                : 'Save Request'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={Boolean(qaChangesRequestTarget)}
        onHide={closeQaChangesModal}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1.05rem' }}>Request Changes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted" style={{ fontSize: '0.9rem' }}>
            Choose who should receive this QA change request. Your note will be appended to the
            request notes either way so it stays with the DBQ.
          </p>
          <div className="text-muted small mb-2">
            {qaChangesRequestTarget?.conditionTitle || qaChangesRequestTarget?.title || 'DBQ request'}
          </div>
          <div className="border rounded-3 p-3 mb-3" style={{ background: '#f8fafc' }}>
            <Form.Check
              type="radio"
              id="qa-request-changes-case-manager"
              name="qa-request-changes-target"
              label="Send back to Case Manager"
              checked={qaChangesRequestDestination === QA_REQUEST_CHANGE_TARGETS.caseManager}
              onChange={() => setQaChangesRequestDestination(QA_REQUEST_CHANGE_TARGETS.caseManager)}
              disabled={requestChangesMutation.isPending}
            />
            <div className="text-muted small ms-4 mb-3">
              Notify the assigned case manager so they can revise the request and decide whether it
              should go back to the provider.
            </div>
            <Form.Check
              type="radio"
              id="qa-request-changes-provider"
              name="qa-request-changes-target"
              label="Send back to Provider"
              checked={qaChangesRequestDestination === QA_REQUEST_CHANGE_TARGETS.provider}
              onChange={() => setQaChangesRequestDestination(QA_REQUEST_CHANGE_TARGETS.provider)}
              disabled={requestChangesMutation.isPending}
            />
            <div className="text-muted small ms-4">
              Notify the assigned provider directly and move the DBQ back into the provider
              correction round.
            </div>
          </div>
          <textarea
            className="form-control"
            rows={6}
            placeholder={
              qaChangesRequestDestination === QA_REQUEST_CHANGE_TARGETS.provider
                ? 'Add the changes you want the provider to address.'
                : 'Add the changes you want the case manager to address.'
            }
            value={qaChangesRequestNote}
            onChange={(event) => setQaChangesRequestNote(event.target.value)}
            disabled={requestChangesMutation.isPending}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeQaChangesModal}
            disabled={requestChangesMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="warning"
            onClick={handleRequestQaChanges}
            disabled={requestChangesMutation.isPending}
          >
            {requestChangesMutation.isPending ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                Requesting...
              </>
            ) : (
              'Request Changes'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={Boolean(reopenDbqTarget)}
        onHide={closeReopenDbqModal}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1.05rem' }}>Reopen Finalized DBQ</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted" style={{ fontSize: '0.9rem' }}>
            Choose whether this finalized DBQ should go back to QA review or re-enter the change
            request loop. Reopening will mark the current finalized packet as stale.
          </p>
          <div className="text-muted small mb-2">
            {reopenDbqTarget?.conditionTitle || reopenDbqTarget?.title || 'DBQ request'}
          </div>
          <div className="border rounded-3 p-3 mb-3" style={{ background: '#f8fafc' }}>
            <Form.Check
              type="radio"
              id="reopen-dbq-return-to-review"
              name="reopen-dbq-mode"
              label="Return To Review"
              checked={reopenDbqMode === REOPEN_FINALIZED_DBQ_MODES.returnToReview}
              onChange={() => setReopenDbqMode(REOPEN_FINALIZED_DBQ_MODES.returnToReview)}
              disabled={reopenFinalizedDbqReviewMutation.isPending}
            />
            <div className="text-muted small ms-4 mb-3">
              Move the DBQ back into Claims Admin review without emailing anyone.
            </div>
            <Form.Check
              type="radio"
              id="reopen-dbq-request-changes"
              name="reopen-dbq-mode"
              label="Request Changes"
              checked={reopenDbqMode === REOPEN_FINALIZED_DBQ_MODES.requestChanges}
              onChange={() => setReopenDbqMode(REOPEN_FINALIZED_DBQ_MODES.requestChanges)}
              disabled={reopenFinalizedDbqReviewMutation.isPending}
            />
            <div className="text-muted small ms-4">
              Reopen the DBQ and send it into the corrections loop.
            </div>
          </div>

          {reopenDbqMode === REOPEN_FINALIZED_DBQ_MODES.requestChanges ? (
            <div className="border rounded-3 p-3 mb-3" style={{ background: '#f8fafc' }}>
              <Form.Check
                type="radio"
                id="reopen-dbq-target-case-manager"
                name="reopen-dbq-target"
                label="Send back to Case Manager"
                checked={reopenDbqDestination === QA_REQUEST_CHANGE_TARGETS.caseManager}
                onChange={() => setReopenDbqDestination(QA_REQUEST_CHANGE_TARGETS.caseManager)}
                disabled={reopenFinalizedDbqReviewMutation.isPending}
              />
              <div className="text-muted small ms-4 mb-3">
                Notify the assigned case manager so they can revise the request and decide whether
                it should go back to the provider.
              </div>
              <Form.Check
                type="radio"
                id="reopen-dbq-target-provider"
                name="reopen-dbq-target"
                label="Send back to Provider"
                checked={reopenDbqDestination === QA_REQUEST_CHANGE_TARGETS.provider}
                onChange={() => setReopenDbqDestination(QA_REQUEST_CHANGE_TARGETS.provider)}
                disabled={reopenFinalizedDbqReviewMutation.isPending}
              />
              <div className="text-muted small ms-4">
                Notify the assigned provider directly and reopen the DBQ in provider corrections.
              </div>
            </div>
          ) : null}

          <textarea
            className="form-control"
            rows={6}
            placeholder={
              reopenDbqMode === REOPEN_FINALIZED_DBQ_MODES.requestChanges
                ? reopenDbqDestination === QA_REQUEST_CHANGE_TARGETS.provider
                  ? 'Add the changes you want the provider to address.'
                  : 'Add the changes you want the case manager to address.'
                : 'Add the reason this finalized DBQ needs to return to review.'
            }
            value={reopenDbqNote}
            onChange={(event) => setReopenDbqNote(event.target.value)}
            disabled={reopenFinalizedDbqReviewMutation.isPending}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeReopenDbqModal}
            disabled={reopenFinalizedDbqReviewMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="warning"
            onClick={handleReopenFinalizedDbq}
            disabled={
              reopenFinalizedDbqReviewMutation.isPending || !String(reopenDbqNote || '').trim()
            }
          >
            {reopenFinalizedDbqReviewMutation.isPending ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                Reopening...
              </>
            ) : (
              'Reopen'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showReviewNotificationModal}
        onHide={() => setShowReviewNotificationModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1.05rem' }}>Send Claim For Review</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted" style={{ fontSize: '0.9rem' }}>
            Select which Claims Admins should be notified. All Claims Admins can still see this
            claim in their dashboard even if they are not selected here.
          </p>

          {claimsAdminOptions.length === 0 ? (
            <Alert variant="warning" className="mb-0">
              No Claims Admin users are available to notify.
            </Alert>
          ) : (
            <>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="text-muted small">
                  {readyForReviewDbqs.length} ready request
                  {readyForReviewDbqs.length !== 1 ? 's' : ''} will be included.
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 text-decoration-none"
                    onClick={() =>
                      setSelectedClaimsAdminUserIds(
                        claimsAdminOptions.map((claimsAdmin) => claimsAdmin.userId),
                      )
                    }
                  >
                    Select all
                  </Button>
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 text-decoration-none"
                    onClick={() => setSelectedClaimsAdminUserIds([])}
                  >
                    Clear
                  </Button>
                </div>
              </div>

              <div className="d-flex flex-column gap-2">
                {claimsAdminOptions.map((claimsAdmin) => (
                  <label
                    key={claimsAdmin.userId}
                    className="d-flex align-items-start gap-2 border rounded p-2"
                    style={{ cursor: 'pointer' }}
                  >
                    <input
                      type="checkbox"
                      className="form-check-input mt-1"
                      checked={selectedClaimsAdminUserIds.includes(claimsAdmin.userId)}
                      onChange={(event) =>
                        handleToggleClaimsAdminSelection(
                          claimsAdmin.userId,
                          event.target.checked,
                        )
                      }
                    />
                    <div>
                      <div style={{ fontSize: '0.9rem', fontWeight: 500 }}>{claimsAdmin.name}</div>
                      <div className="text-muted small">{claimsAdmin.email}</div>
                    </div>
                  </label>
                ))}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowReviewNotificationModal(false)}
            disabled={sendClaimDbqsForQaReviewMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="dark"
            onClick={handleSendClaimForReview}
            disabled={
              sendClaimDbqsForQaReviewMutation.isPending ||
              claimsAdminOptions.length === 0 ||
              selectedClaimsAdminUserIds.length === 0
            }
          >
            {sendClaimDbqsForQaReviewMutation.isPending ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                Sending...
              </>
            ) : (
              'Send For Review'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DbqTab;
